import React, { useEffect, useRef, useState } from 'react';
import { layoutDay } from '../utils/dayLayout';
import { stripIcon } from './EventIcon';
import { uses12h, weekStartsOn } from '../utils/timeFormat';
import { claimSwipe, releaseSwipe } from '../utils/swipeOwner';
import { useNavigate } from 'react-router-dom';
import {
  format, addDays, subDays, startOfWeek, parseISO, isToday as fnsIsToday,
} from 'date-fns';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useT, DATE_LOCALES } from '../i18n';
import { readableText } from '../utils/color';
import { weekEventsCache } from '../utils/viewCache';
import { a11y } from '../utils/a11yLabels';

const START_HOUR = 6;
const END_HOUR   = 23;
const HOURS      = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);

const SLOT_H = 44;
const TIME_COL_W = 40;

// Loraine, in New York: "the times on the left side of the day is still 0-24
// format. I don't think it ever changed for me." She was right — the day's
// events had learned to ask the phone which clock the country uses, but this
// column never did: it always printed 07:00. In the United States that is not
// a time, it is a puzzle.
function hourLabel(h) {
  if (!uses12h()) return String(h).padStart(2, '0') + ':00';
  const suffix = h < 12 ? 'AM' : 'PM';
  const n = h % 12 === 0 ? 12 : h % 12;
  return n + ' ' + suffix;
}

function getTodayStr() { return format(new Date(), 'yyyy-MM-dd'); }

function getMonday(dateStr) {
  return format(startOfWeek(parseISO(dateStr), { weekStartsOn: weekStartsOn() }), 'yyyy-MM-dd');
}

const evColor = ev => ev.subCalendar?.color || 'var(--color-primary)';

// Two usages:
// - Week grid (default): fetches the whole week itself, own week navigation.
// - Single-day grid: pass `day` (YYYY-MM-DD) + `dayEvents` (already loaded by
//   TodayView) — no fetching, no internal navigation, taller slots.
export default function SlotGridView({ initialDate, activeSubCal, day, dayEvents }) {
  const { user }  = useAuth();
  const T         = useT(user?.language);
  const A         = a11y(user?.language);   // spoken names for the ‹ › arrows
  const locale    = DATE_LOCALES[user?.language] || DATE_LOCALES['it'];
  const navigate  = useNavigate();

  const isSingleDay = !!day;
  const [weekStart, setWeekStart] = useState(getMonday(initialDate || getTodayStr()));
  // Starts from the same cache the week list fills — it is the very same
  // request. Switching to the grid used to mean a spinner every single time,
  // even for a week just looked at.
  const [evMap,     setEvMap]     = useState(() => weekEventsCache.get(weekStart) || {});
  const [fetching,  setFetching]  = useState(() => !weekEventsCache.get(weekStart));
  const nowIndicatorRef = useRef(null);

  const slotH  = isSingleDay ? 60 : SLOT_H;
  const today  = getTodayStr();
  const dates  = isSingleDay
    ? [day]
    : Array.from({ length: 7 }, (_, i) => format(addDays(parseISO(weekStart), i), 'yyyy-MM-dd'));
  const totalH = HOURS.length * slotH;
  const loading = isSingleDay ? false : fetching;

  // Respect the sub-calendar filter chips shared with the other views
  const eventsOf = (d) => {
    const src = isSingleDay ? (dayEvents || []) : (evMap[d] || []);
    return src.filter(ev => !activeSubCal || ev.subCalendar?.id === activeSubCal);
  };

  // Fetch events for the whole week in one request (week mode only)
  useEffect(() => {
    if (isSingleDay) return undefined;
    let cancelled = false;
    const cached = weekEventsCache.get(weekStart);
    if (cached) setEvMap(cached);
    setFetching(!cached);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get('/events/week', { params: { tz, start: weekStart } })
      .then(res => {
        if (cancelled) return;
        weekEventsCache.set(weekStart, res.data || {});
        setEvMap(res.data || {});
      })
      // Never blank the grid on a failed refresh: an empty grid is
      // indistinguishable from "you are free all week", which would be a lie.
      .catch(() => {})
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [weekStart, isSingleDay]);

  // Scroll to current time (or 9 AM) after render
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      if (nowIndicatorRef.current) {
        nowIndicatorRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
      } else {
        const pc = document.querySelector('.page-content');
        if (pc) pc.scrollTop = Math.max(0, (9 - START_HOUR) * slotH - 80);
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [loading]);

  // Current time in px
  const nowObj = new Date();
  const nowH   = nowObj.getHours() + nowObj.getMinutes() / 60;
  const nowPx  = (nowH >= START_HOUR && nowH <= END_HOUR)
    ? (nowH - START_HOUR) * slotH : null;

  function handleColumnClick(e, dateStr) {
    // Ignore clicks that originated on an event block
    if (e.target.closest('[data-ev]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y    = e.clientY - rect.top;
    const hour = Math.min(END_HOUR - 1, Math.max(START_HOUR, Math.floor(y / slotH) + START_HOUR));
    navigate(`/new?date=${dateStr}&time=${String(hour).padStart(2, '0')}:00`);
  }

  // Navigation labels
  const wsObj    = parseISO(weekStart);
  const weObj    = addDays(wsObj, 6);
  const navLabel = `${format(wsObj, 'd MMM', { locale })} – ${format(weObj, 'd MMM yyyy', { locale })}`;

  // The week we are in, so the way back knows where back is.
  const thisWeekStart = format(startOfWeek(new Date(), { weekStartsOn: weekStartsOn() }), 'yyyy-MM-dd');

  // A sideways drag moves the week here too — the planner was the last screen
  // still asking for a button. Only in week mode: the single-day planner lives
  // inside Today, which owns the gesture already.
  useEffect(() => {
    if (isSingleDay) return undefined;
    claimSwipe('planner');
    let x0 = 0, y0 = 0, t0 = 0, live = false;
    function start(e) {
      const t = e.touches && e.touches[0];
      if (!t) { live = false; return; }
      if (e.target?.closest?.('button, a, input, textarea, select, [role="button"], [data-ev]')) { live = false; return; }
      x0 = t.clientX; y0 = t.clientY; t0 = e.timeStamp; live = true;
    }
    function end(e) {
      if (!live) return;
      live = false;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (e.timeStamp - t0 > 800) return;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.8) return;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const back = rtl ? dx < 0 : dx > 0;
      setWeekStart(w => format(back ? subDays(parseISO(w), 7) : addDays(parseISO(w), 7), 'yyyy-MM-dd'));
    }
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    return () => {
      releaseSwipe('planner');
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
    };
  }, [isSingleDay]);

  // All-day events
  const hasAllDay = dates.some(d => eventsOf(d).some(e => e.allDay));

  return (
    <div>
      {/* ── Navigation (single-day mode reuses the parent's date nav) ──── */}
      {/* No arrows, like everywhere else: a sideways drag moves the week.
          Silvia found the planner still had them — it was the last screen that
          did. The hidden buttons stay for TalkBack, which takes the gesture for
          itself, and the round arrow on the left goes back to the week you are
          actually in. */}
      {!isSingleDay && (
        <div className="date-nav" style={{ marginBottom: 6, position: 'relative' }}>
          <button className="visually-hidden" onClick={() =>
            setWeekStart(w => format(subDays(parseISO(w), 7), 'yyyy-MM-dd'))
          }>{A.prevWeek}</button>
          {weekStart !== thisWeekStart && (
            <button
              type="button"
              onClick={() => setWeekStart(thisWeekStart)}
              aria-label={T.nav.today}
              title={T.nav.today}
              style={{
                position: 'absolute', insetInlineStart: 0, top: '50%', transform: 'translateY(-50%)',
                width: 30, height: 30, borderRadius: '50%', border: 'none',
                background: 'var(--color-primary)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0, zIndex: 2,
              }}
            >
              <span className="rtl-flip" style={{ display: 'flex' }}>
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
              </span>
            </button>
          )}
          <div className="date-nav-center" style={{ paddingInlineStart: weekStart !== thisWeekStart ? 30 : 0, paddingInlineEnd: weekStart !== thisWeekStart ? 30 : 0 }}>
            <div className="today-date" style={{ fontSize: '0.88rem' }}>{navLabel}</div>
          </div>
          <button className="visually-hidden" onClick={() =>
            setWeekStart(w => format(addDays(parseISO(w), 7), 'yyyy-MM-dd'))
          }>{A.nextWeek}</button>
        </div>
      )}

      {/* ── Legend + hint ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, margin: '0 0 8px', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: 'rgba(82,183,136,0.35)', border: '1px solid var(--color-primary)' }} />
          {T.today?.slotsFree || 'Libero'}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: 'var(--color-primary)', opacity: 0.85 }} />
          {T.today?.slotsBusy || 'Occupato'}
        </span>
        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
          {T.today?.slotsHint || 'Tocca slot libero → nuovo evento'}
        </span>
      </div>

      {loading && <div className="spinner" />}

      {!loading && (
        <>
          {/* ── Day headers ───────────────────────────────────────────── */}
          <div style={{ display: 'flex', marginBottom: 0, borderBottom: '1.5px solid var(--color-border)' }}>
            <div style={{ width: TIME_COL_W, flexShrink: 0 }} />
            {dates.map(d => {
              const obj     = parseISO(d);
              const isToday = d === today;
              return (
                <div key={d}
                  style={{
                    flex: 1,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: '3px 0 5px',
                    background: isToday ? 'rgba(82,183,136,0.07)' : 'transparent',
                }}>
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 600,
                    color: isToday ? 'var(--color-primary)' : 'var(--color-text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.03em',
                  }}>
                    {format(obj, 'EEE', { locale })}
                  </span>
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: isToday ? '#fff' : 'var(--color-text)',
                    background: isToday ? 'var(--color-primary)' : 'transparent',
                    borderRadius: '50%',
                    width:  22,
                    height: 22,
                    lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {format(obj, 'd')}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── All-day strip ─────────────────────────────────────────── */}
          {hasAllDay && (
            <div style={{
              display: 'flex',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg-alt, rgba(0,0,0,0.02))',
            }}>
              <div style={{
                width: TIME_COL_W, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                paddingInlineEnd: 5, fontSize: '0.52rem', color: 'var(--color-text-muted)',
              }}>all</div>
              {dates.map(d => (
                <div key={d} style={{ flex: 1, borderInlineStart: '1px solid var(--color-border)', padding: '2px 2px', minHeight: 22 }}>
                  {eventsOf(d).filter(e => e.allDay).map(ev => (
                    <div key={ev.id} data-ev="1"
                      onClick={e => { e.stopPropagation(); navigate(`/edit/${ev.id}`, { state: { event: ev } }); }}
                      style={{
                        background: evColor(ev),
                        borderRadius: 3, color: readableText(evColor(ev)),
                        fontSize: '0.58rem', fontWeight: 600,
                        padding: '1px 4px', marginBottom: 1,
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        cursor: 'pointer',
                      }}
                    translate="no">{stripIcon(ev.title)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* ── Main time grid ────────────────────────────────────────── */}
          <div style={{ display: 'flex', borderTop: hasAllDay ? 'none' : '1px solid var(--color-border)' }}>
            {/* Time labels */}
            <div style={{ width: TIME_COL_W, flexShrink: 0, position: 'relative', height: totalH }}>
              {HOURS.map(h => (
                <div key={h} style={{
                  position: 'absolute',
                  top: (h - START_HOUR) * slotH,
                  right: 5, left: 0, height: slotH,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                  paddingTop: 3,
                  fontSize: '0.58rem', color: 'var(--color-text-muted)', lineHeight: 1,
                }}>
                  {hourLabel(h)}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {dates.map(d => {
              const timedEvs = eventsOf(d).filter(e => !e.allDay);
              const layout   = layoutDay(timedEvs, slotH);
              const isToday  = d === today;

              // Build a set of "busy" hours so free slots can be highlighted
              const busyHours = new Set();
              timedEvs.forEach(ev => {
                const s = new Date(ev.start);
                const e = ev.end ? new Date(ev.end) : new Date(+s + 3_600_000);
                const sh = Math.max(START_HOUR, s.getHours());
                let ehRaw = e.getHours() + e.getMinutes() / 60;
                if (ehRaw <= s.getHours() + s.getMinutes() / 60) ehRaw = END_HOUR; // crosses midnight
                const eh = Math.min(END_HOUR - 1, Math.ceil(ehRaw));
                for (let h = sh; h < eh; h++) busyHours.add(h);
              });

              return (
                <div key={d}
                  onClick={e => handleColumnClick(e, d)}
                  style={{
                    flex: 1, position: 'relative',
                    height: totalH,
                    borderInlineStart: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {/* Hour rows: free = light green tint, busy = subtle warm tint */}
                  {HOURS.map(h => (
                    <div key={h} style={{
                      position: 'absolute',
                      top: (h - START_HOUR) * slotH,
                      left: 0, right: 0, height: slotH,
                      background: busyHours.has(h)
                        ? 'rgba(196,97,74,0.06)'
                        : isToday
                          ? 'rgba(82,183,136,0.07)'
                          : 'rgba(82,183,136,0.03)',
                    }} />
                  ))}

                  {/* Horizontal hour lines */}
                  {HOURS.map(h => (
                    <div key={`l${h}`} style={{
                      position: 'absolute',
                      top: (h - START_HOUR) * slotH,
                      left: 0, right: 0, height: 1,
                      background: 'var(--color-border)', // themed — the old rgba(0,0,0) was invisible in dark mode
                      zIndex: 1,
                    }} />
                  ))}

                  {/* Event blocks */}
                  {timedEvs.map(ev => {
                    const geo = layout.get(ev.id);
                    if (!geo) return null;
                    const color = evColor(ev);
                    return (
                      <div key={ev.id} data-ev="1"
                        onClick={e => { e.stopPropagation(); navigate(`/edit/${ev.id}`, { state: { event: ev } }); }}
                        style={{
                          position: 'absolute',
                          top: geo.top + 1,
                          // One lane = the whole column, exactly as before. Two
                          // or more = the column shared, so nothing is hidden.
                          left:  `calc(${(geo.lane / geo.lanes) * 100}% + 2px)`,
                          width: `calc(${100 / geo.lanes}% - 4px)`,
                          height: geo.height - 2,
                          background: color,
                          borderRadius: 4,
                          overflow: 'hidden',
                          padding: '2px 4px',
                          color: readableText(color),
                          fontSize: '0.58rem',
                          fontWeight: 600, lineHeight: 1.25,
                          cursor: 'pointer', zIndex: 2,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.22)',
                        }}
                      >
                        {/* Jodi, in the United States: "Wayne's events show on my
                            calendar (in terms of something is scheduled), but on the
                            event it is blank. I have to tap to open it and then I can
                            see what it is."

                            The rule used to be: write the name only if the block is
                            at least nine tenths of an hour tall. Anything shorter —
                            a half-hour appointment, a school run, most of a normal
                            day — was a coloured rectangle with nothing in it. The app
                            HAD the name and was choosing not to say it, which is the
                            same fault as claiming something happened when it did not:
                            silence that looks like information.

                            Now the name is always written. A block too short for a
                            full line gets tighter padding and a smaller line so that
                            at least the beginning shows, and the rest is clipped —
                            three visible letters beat nothing at all. */}
                        {(() => {
                          const tight = geo.height < 20;
                          return (
                            <span translate="no" style={{
                              display: 'block', overflow: 'hidden', whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              lineHeight: tight ? 1 : 1.25,
                              marginTop: tight ? -1 : 0,
                              fontSize: tight ? '0.52rem' : undefined,
                            }}>
                              {stripIcon(ev.title)}
                            </span>
                          );
                        })()}
                      </div>
                    );
                  })}

                  {/* Current-time indicator */}
                  {isToday && nowPx != null && (
                    <div
                      ref={nowIndicatorRef}
                      style={{
                        position: 'absolute',
                        top: nowPx, left: -1, right: 0,
                        height: 2, background: '#e53935',
                        zIndex: 3, pointerEvents: 'none',
                      }}
                    >
                      <div style={{
                        position: 'absolute', left: -3, top: -3,
                        width: 8, height: 8, borderRadius: '50%',
                        background: '#e53935',
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ height: 24 }} />
        </>
      )}
    </div>
  );
}
